// /app/frontend/src/components/PushNotifications.jsx
// Push Notification Manager Component

import { useState, useEffect } from "react";
import { Bell, BellOff, BellRing } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api } from "@/App";

// Check if push notifications are supported
const isPushSupported = () => {
  return 'serviceWorker' in navigator && 'PushManager' in window;
};

// Convert VAPID key from base64 to Uint8Array
const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

export const usePushNotifications = () => {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [permission, setPermission] = useState('default');
  
  useEffect(() => {
    checkSupport();
  }, []);
  
  const checkSupport = async () => {
    if (!isPushSupported()) {
      setIsSupported(false);
      return;
    }
    
    setIsSupported(true);
    setPermission(Notification.permission);
    
    // Check if already subscribed
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      setIsSubscribed(!!subscription);
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };
  
  const subscribe = async () => {
    if (!isSupported) {
      toast.error("Le notifiche push non sono supportate");
      return false;
    }
    
    setIsLoading(true);
    
    try {
      // Request permission
      const permission = await Notification.requestPermission();
      setPermission(permission);
      
      if (permission !== 'granted') {
        toast.error("Permesso notifiche negato");
        setIsLoading(false);
        return false;
      }
      
      // Register service worker
      const registration = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;
      
      // Get VAPID public key from backend
      const vapidResponse = await api.get('/push/vapid-key');
      const vapidPublicKey = vapidResponse.data.public_key;
      
      if (!vapidPublicKey) {
        toast.error("Configurazione push non disponibile");
        setIsLoading(false);
        return false;
      }
      
      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });
      
      // Send subscription to backend
      await api.post('/push/subscribe', {
        subscription: subscription.toJSON()
      });
      
      setIsSubscribed(true);
      toast.success("Notifiche push attivate!");
      return true;
      
    } catch (error) {
      console.error('Push subscription error:', error);
      toast.error("Errore nell'attivazione delle notifiche");
      return false;
    } finally {
      setIsLoading(false);
    }
  };
  
  const unsubscribe = async () => {
    setIsLoading(true);
    
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        await subscription.unsubscribe();
        await api.post('/push/unsubscribe');
      }
      
      setIsSubscribed(false);
      toast.success("Notifiche push disattivate");
      return true;
      
    } catch (error) {
      console.error('Push unsubscribe error:', error);
      toast.error("Errore nella disattivazione");
      return false;
    } finally {
      setIsLoading(false);
    }
  };
  
  const toggleSubscription = async () => {
    if (isSubscribed) {
      return await unsubscribe();
    } else {
      return await subscribe();
    }
  };
  
  return {
    isSupported,
    isSubscribed,
    isLoading,
    permission,
    subscribe,
    unsubscribe,
    toggleSubscription
  };
};

// Push Notification Toggle Button
export const PushNotificationToggle = ({ size = "md" }) => {
  const { isSupported, isSubscribed, isLoading, toggleSubscription } = usePushNotifications();
  
  if (!isSupported) {
    return null;
  }
  
  const sizeClasses = {
    sm: "p-2",
    md: "p-2.5",
    lg: "p-3"
  };
  
  const iconSize = {
    sm: "w-4 h-4",
    md: "w-5 h-5",
    lg: "w-6 h-6"
  };
  
  return (
    <button
      onClick={toggleSubscription}
      disabled={isLoading}
      className={`${sizeClasses[size]} rounded-full transition-all ${
        isSubscribed 
          ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg shadow-green-200/50' 
          : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
      } ${isLoading ? 'opacity-50 cursor-wait' : ''}`}
      title={isSubscribed ? "Notifiche attive" : "Attiva notifiche"}
    >
      {isLoading ? (
        <div className={`${iconSize[size]} border-2 border-current border-t-transparent rounded-full animate-spin`} />
      ) : isSubscribed ? (
        <BellRing className={`${iconSize[size]} animate-pulse`} />
      ) : (
        <BellOff className={iconSize[size]} />
      )}
    </button>
  );
};

// Push Notification Settings Card
export const PushNotificationSettings = () => {
  const { isSupported, isSubscribed, isLoading, permission, toggleSubscription } = usePushNotifications();
  const [testingPush, setTestingPush] = useState(false);
  
  const handleTestPush = async () => {
    if (!isSubscribed) {
      toast.error("Attiva prima le notifiche");
      return;
    }
    
    setTestingPush(true);
    try {
      await api.post('/push/test');
      toast.success("Notifica di test inviata!");
    } catch (error) {
      toast.error("Errore nell'invio del test");
    } finally {
      setTestingPush(false);
    }
  };
  
  if (!isSupported) {
    return (
      <div className="p-4 bg-gray-50 rounded-xl text-center text-gray-500">
        <BellOff className="w-8 h-8 mx-auto mb-2 text-gray-400" />
        <p className="text-sm">Le notifiche push non sono supportate su questo browser</p>
      </div>
    );
  }
  
  return (
    <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-full ${isSubscribed ? 'bg-green-100' : 'bg-gray-100'}`}>
            {isSubscribed ? (
              <BellRing className="w-5 h-5 text-green-600" />
            ) : (
              <Bell className="w-5 h-5 text-gray-500" />
            )}
          </div>
          <div>
            <p className="font-semibold text-gray-900">Notifiche Push</p>
            <p className="text-xs text-gray-500">
              {isSubscribed 
                ? "Ricevi notifiche per regali e connessioni" 
                : "Attiva per non perdere nessun aggiornamento"}
            </p>
          </div>
        </div>
        
        <Button
          onClick={toggleSubscription}
          disabled={isLoading}
          variant={isSubscribed ? "outline" : "default"}
          size="sm"
          className={isSubscribed ? "" : "bg-gradient-to-r from-amber-500 to-orange-500 text-white"}
        >
          {isLoading ? (
            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
          ) : isSubscribed ? (
            "Disattiva"
          ) : (
            "Attiva"
          )}
        </Button>
      </div>
      
      {/* Test notification button - only show when subscribed */}
      {isSubscribed && (
        <div className="mt-3 pt-3 border-t border-amber-200/50">
          <Button
            onClick={handleTestPush}
            disabled={testingPush}
            variant="outline"
            size="sm"
            className="w-full text-amber-600 border-amber-300 hover:bg-amber-100"
          >
            {testingPush ? (
              <div className="w-4 h-4 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mr-2" />
            ) : (
              <Bell className="w-4 h-4 mr-2" />
            )}
            Invia notifica di test
          </Button>
        </div>
      )}
      
      {permission === 'denied' && (
        <p className="mt-2 text-xs text-red-500">
          ⚠️ Hai bloccato le notifiche. Abilita dalle impostazioni del browser.
        </p>
      )}
    </div>
  );
};

export default PushNotificationToggle;
